import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import classNames from 'classnames';
import noop from 'lodash/fp/noop';
import toNumber from 'lodash/fp/toNumber';
import SidebarFooter from './SidebarFooter';
import SidebarBackdrop from './SidebarBackdrop';
import SidebarFullscreenToggle from './SidebarFullscreenToggle';
import SidebarCloseButton from './SidebarCloseButton';
import Resizer from '../resizer/Resizer';
import useWindowResize from '../../hooks/useWindowResize';
import useEsc from '../../hooks/useEsc';
import getWidthInBoundaries from '../../utils/getWidthInBoundaries';
import SmoothScrollbars from '../smoothScrollbars/SmoothScrollbars';
const DEFAULT_WIDTH = 350;
const DEFAULT_MIN_WIDTH = 100;
const DEFAULT_MAX_WIDTH = 800;
const DEFAULT_SWITCH_MODE_BREAKPOINT = 0;
// Values define the min and max width for the module-content
// where the fluid sidebar automatically changes to "fly" and back.
const MIN_MODULE_CONTENT_WIDTH = 400;
const MAX_MODULE_CONTENT_WIDTH = 800;
const ANIMATION_DURATION = 0.14;
const RESIZE_THROTTLE = 500;
const SidebarPosition = {
    LEFT: 'left',
    RIGHT: 'right',
};
const SidebarMode = {
    MODE_FLY: 'fly',
    MODE_FLUID: 'fluid',
};
const animationVariants = {
    sidebarEnter: ({ fromRight }) => {
        return {
            x: fromRight ? '100%' : '-100%',
            opacity: 0,
        };
    },
    sidebarVisible: ({ fromRight, closed }) => {
        return {
            x: !closed ? 0 : fromRight ? '100%' : '-100%',
            opacity: closed ? 0 : 1,
            display: closed ? 'none' : 'block',
        };
    },
    sidebarExit: ({ fromRight }) => {
        return {
            x: fromRight ? '100%' : '-100%',
            opacity: 0,
        };
    },
};
const Sidebar = (props) => {
    const { fly = false, closed = false, title = '', footer, resizable = false, position, width = DEFAULT_WIDTH, minWidth = DEFAULT_MIN_WIDTH, maxWidth = DEFAULT_MAX_WIDTH, hasBackdrop = false, hasSmoothScrollbar = false, makeBackdropVisible = false, enableFullscreenToggle = false, fullscreenToggleTooltip, openInFullscreen = false, showHeaderBorder = false, disableClose = false, bodyRef, headerButtons, switchModeBreakpoint = DEFAULT_SWITCH_MODE_BREAKPOINT, onClose = noop, onResizeEnd = noop, onFullScreenChange = noop, onBackdropClick = noop, disableEsc = false, titleClassName = '', bodyClassName = '', headerClassName = '', footerClassName = '', backdropClassName = '', className = '', children, ...remainingProps } = props;
    const [internalWidth, setInternalWidth] = useState(toNumber(width));
    const [sidebarMode, setSidebarMode] = useState(fly ? Sidebar.MODE_FLY : Sidebar.MODE_FLUID);
    const [isFullscreen, setIsFullscreen] = useState(openInFullscreen);
    const [isResize, setIsResize] = useState(false);
    const [isSplit, setIsSplit] = useState(false);
    const [isRight, setIsRight] = useState(position === SidebarPosition.RIGHT);
    const sidebarRef = useRef(null);
    // Used to keep the external reference to the module-content to avoid querying the DOM
    // periodically on window resize
    const moduleContentRef = useRef(null);
    // Position fallback in case the position property is not defined but the sidebar mode is fly
    // and we want to animate it, we nee to know the direction from where th sidebar is coming from.
    // That's why we check the ApplicationLayoutSidebar class if the sidebar inside is right aligned
    // or not and us this value instead.
    // Note: the position need to be set properly whe using the resize functionality to know on which
    // side of the sidebar to attach the resizer handle.
    useEffect(() => {
        if (sidebarRef.current && sidebarMode === Sidebar.MODE_FLY) {
            const sidebarLayout = sidebarRef.current.parentNode;
            if (sidebarLayout?.className.includes('right')) {
                setIsRight(true);
            }
        }
    }, [sidebarRef.current, sidebarMode]);
    // Keep the previous closed state in order to prevent animation when switching sidebar mode
    const [previousClosed, setPreviousClosed] = useState(closed);
    if (previousClosed !== closed) {
        setPreviousClosed(closed);
    }
    // Update internal width from outside
    const [previousWidth, setPreviousWidth] = useState(width);
    if (previousWidth !== width) {
        console.log('update internal width');
        setInternalWidth(toNumber(width));
        setPreviousWidth(width);
    }
    // Switch between mode "fly" and "fluid"
    useWindowResize(() => adaptSidebarMode(), RESIZE_THROTTLE);
    // Initially check breakpoint after mounting the component
    useEffect(() => adaptSidebarMode(), []);
    // When Sidebar opens, check for available module-content width and
    // switch sidebar mode to fly in case
    useEffect(() => {
        if (!closed) {
            setTimeout(() => adaptSidebarMode());
        }
    }, [closed]);
    useEffect(() => {
        const moduleContent = document.querySelector('.module-content');
        if (moduleContent) {
            moduleContentRef.current = moduleContent;
        }
    }, []);
    // Close the fullscreen with "esc"
    useEsc(() => {
        if (!disableEsc && enableFullscreenToggle && isFullscreen) {
            setIsFullscreen(false);
            onFullScreenChange(false);
        }
    });
    const adaptSidebarMode = () => {
        const moduleContentWidth = moduleContentRef.current?.clientWidth;
        if (fly || switchModeBreakpoint === DEFAULT_SWITCH_MODE_BREAKPOINT) {
            return;
        }
        const isModuleContentTooSmall = moduleContentWidth && moduleContentWidth <= MIN_MODULE_CONTENT_WIDTH;
        const isModuleContentBigEnough = moduleContentWidth && moduleContentWidth > MAX_MODULE_CONTENT_WIDTH;
        const isWindowSmallerThanBreakpoint = window.innerWidth <= switchModeBreakpoint;
        // switch to fly when module-content is less than x pixel and back if it is bigger
        if (isModuleContentTooSmall || isWindowSmallerThanBreakpoint) {
            setSidebarMode(Sidebar.MODE_FLY);
            return;
        }
        // if module-content is bigger and is initially fluid set back to initial fluid state
        if (isModuleContentBigEnough && !isWindowSmallerThanBreakpoint) {
            setSidebarMode(Sidebar.MODE_FLUID);
            return;
        }
    };
    const handleResize = (diff) => {
        const wasSplit = isSplit;
        const halfWindowWidth = window.innerWidth * 0.5;
        const usedMaxWidth = maxWidth || halfWindowWidth;
        // Check for sidebar width if it is half the window size. If it was before but the sidebar was resized so it is
        // no longer half window size, set the sidebar with to half window size to avoid jumping sidebar to old width
        setInternalWidth(oldWidth => {
            const updatedWidth = position === Sidebar.RIGHT ? oldWidth + diff : oldWidth - diff;
            const newWidth = getWidthInBoundaries(minWidth, usedMaxWidth, updatedWidth);
            const newIsSplit = newWidth === halfWindowWidth;
            setIsSplit(newIsSplit);
            return wasSplit && !isSplit ? halfWindowWidth : newWidth;
        });
        adaptSidebarMode();
    };
    const handleResizeStart = () => {
        const body = document.body;
        body?.classList.add('pointer-events-none');
        setIsResize(true);
    };
    const handleResizeEnd = () => {
        const body = document.body;
        body?.classList.remove('pointer-events-none');
        setIsResize(false);
        onResizeEnd();
    };
    const handleFullscreenChange = () => {
        const newFullscreenState = !isFullscreen;
        setIsFullscreen(newFullscreenState);
        onFullScreenChange(newFullscreenState);
    };
    const wrapperClasses = classNames('Sidebar', className, closed && 'closed', isFullscreen && 'width-100vw sidebar-fullscreen', isSplit && !isFullscreen && 'max-width-50vw width-50vw', sidebarMode === SidebarMode.MODE_FLY ? 'fly' : isFullscreen ? 'fly' : 'fluid');
    const headerClassNames = classNames('SidebarHeader', headerClassName, showHeaderBorder && 'show-border');
    const titleClassNames = classNames('SidebarTitle', titleClassName);
    const bodyClassNames = classNames('SidebarBody', bodyClassName);
    const resizeLimitClasses = classNames('SidebarResizeLimit', isResize && 'display-block');
    const resizeIndicatorPosition = maxWidth || window.innerWidth * 0.5;
    const resizeLimitStyle = isRight ? { right: resizeIndicatorPosition } : { left: resizeIndicatorPosition };
    const sidebarContent = (_jsxs(_Fragment, { children: [_jsx("div", { className: resizeLimitClasses, style: resizeLimitStyle }), _jsxs("div", { className: 'SidebarContent', children: [_jsxs("div", { className: headerClassNames, children: [_jsx("div", { className: titleClassNames, children: title }), _jsxs("div", { className: 'SidebarButtons non-printable close', children: [headerButtons, enableFullscreenToggle && (_jsx(SidebarFullscreenToggle, { isFullscreen: isFullscreen, onClick: handleFullscreenChange, tooltip: fullscreenToggleTooltip })), !disableClose && (headerButtons || enableFullscreenToggle) && (_jsx("div", { className: 'SidebarButtons-spacer' })), !disableClose && _jsx(SidebarCloseButton, { onClick: onClose })] })] }), hasSmoothScrollbar ? (_jsx(SmoothScrollbars, { slideIn: true, className: bodyClassNames, ref: bodyRef, children: children })) : (_jsx("div", { className: bodyClassNames, ref: bodyRef, children: children })), _jsx(SidebarFooter, { footer: footer, className: footerClassName })] }), resizable && (_jsx(Resizer, { onResizeStart: handleResizeStart, onResize: handleResize, onResizeEnd: handleResizeEnd, direction: Resizer.HORIZONTAL, position: isRight ? Resizer.LEFT : Resizer.RIGHT }))] }));
    return (_jsxs(_Fragment, { children: [_jsx(AnimatePresence, { initial: true, exitBeforeEnter: true, children: _jsx(motion.div, { ...remainingProps, initial: previousClosed && 'sidebarEnter', animate: 'sidebarVisible', 
                    // Cannot exit animation as sidebar is moved to offscreen by CSS class, means is still mounted
                    exit: 'sidebarExit', variants: animationVariants, custom: { fromRight: isRight, closed }, transition: { duration: ANIMATION_DURATION }, className: wrapperClasses, style: { width: internalWidth }, ref: sidebarRef, children: sidebarContent }) }), hasBackdrop && !closed && (_jsx(SidebarBackdrop, { className: backdropClassName, makeBackdropVisible: makeBackdropVisible, onClick: onBackdropClick }))] }));
};
Sidebar.LEFT = SidebarPosition.LEFT;
Sidebar.RIGHT = SidebarPosition.RIGHT;
Sidebar.MODE_FLY = SidebarMode.MODE_FLY;
Sidebar.MODE_FLUID = SidebarMode.MODE_FLUID;
export default Sidebar;
